import React, { useEffect, useMemo, useState } from "react";
import {
  GridLayout,
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { Track } from "livekit-client";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import CloseIcon from "@mui/icons-material/Close";
import { detectDevice } from "../utils/deviceDetection";
import { fetchLiveStream, joinLivestream } from "../functions/functions";
import { ImagePrefix } from "../functions/client";

var CryptoJS = require("crypto-js");

const containerStyle = {
  height: "100vh",
  width: "100%",
  background: "#0b0f19",
};

function buildMediaUrl(path) {
  if (!path) return null;
  return `${ImagePrefix}/${path}`;
}

/** Prefer raster thumbnail for blur; fall back to avatar when thumb is video or missing. */
function lobbyBackgroundPath(stream) {
  if (!stream) return null;
  const thumb = stream.thumbnail;
  if (thumb && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(thumb)) return thumb;
  return stream.user?.avatar || null;
}

function displayNameFromStream(stream) {
  if (!stream) return "";
  const u = stream.user;
  if (u?.firstName || u?.lastName) {
    return [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  }
  const p = stream.participants?.[0];
  return p?.name || "";
}

/** LiveKit URL + token from POST /join response (no env fallback). */
function pickJoinCredentials(data) {
  if (!data) return { url: null, token: null };
  const url =
    data.liveKitUrl ||
    data.livekitUrl ||
    data.live_kit_url ||
    data.wssUrl ||
    data.url ||
    null;
  const token =
    data.liveKitToken ||
    data.livekitToken ||
    data.live_kit_token ||
    data.accessToken ||
    data.token ||
    null;
  return { url, token };
}

const pub = (name) => `${process.env.PUBLIC_URL || ""}/${name}`;
const LIVESTREAM_REACTION_SRC = pub("livestream-reaction.svg");
const LIVESTREAM_COMMENT_SRC = pub("livestream-comment.svg");
const LIVESTREAM_SEND_SRC = pub("livestream-send.svg");
const LIVESTREAM_EYE_SRC = pub("livestream-eye.svg");
const LIVESTREAM_GENDER_MALE_SRC = pub("livestream-gender-male.svg");
const LIVESTREAM_GENDER_FEMALE_SRC = pub("livestream-gender-female.svg");
const LIVESTREAM_GENDER_OTHER_SRC = pub("livestream-gender-other.svg");

/** @returns {string|null} public URL for gender icon, or null if unknown */
function genderIconSrc(gender) {
  const g = String(gender || "")
    .toLowerCase()
    .trim();
  if (g === "female" || g === "f" || g === "woman")
    return LIVESTREAM_GENDER_FEMALE_SRC;
  if (g === "male" || g === "m" || g === "man")
    return LIVESTREAM_GENDER_MALE_SRC;
  if (g === "other" || g === "o") return LIVESTREAM_GENDER_OTHER_SRC;
  return LIVESTREAM_GENDER_MALE_SRC;
}

/** Top-left pill: host avatar, name, gender, live participant count. */
function HostPresencePill({ stream, top = 16 }) {
  if (!stream) return null;
  const name = displayNameFromStream(stream) || "Host";
  const avatarUrl = buildMediaUrl(stream?.user?.avatar);
  const genderSrc = genderIconSrc(stream?.user?.gender);
  const liveCount = stream.liveParticipantCount ?? 0;

  return (
    <Box
      sx={{
        position: "absolute",
        top,
        left: 16,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.25,
        py: 0.75,
        pr: 1.5,
        maxWidth: "min(320px, calc(100% - 32px))",
        borderRadius: 999,
        bgcolor: "rgba(48, 48, 52, 0.78)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
      }}
    >
      <Avatar
        src={avatarUrl || undefined}
        alt=""
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          border: "2px solid #fff",
          fontSize: "0.95rem",
          fontWeight: 700,
          bgcolor: "rgba(255,255,255,0.15)",
        }}
      >
        {name.charAt(0)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            variant="subtitle2"
            component="p"
            sx={{
              m: 0,
              color: "#fff",
              fontWeight: 700,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {name}
          </Typography>
          {genderSrc ? (
            <Box
              component="img"
              src={genderSrc}
              alt=""
              aria-hidden
              sx={{
                width: 18,
                height: 18,
                flexShrink: 0,
                display: "block",
                objectFit: "contain",
              }}
            />
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.2,
          }}
        >
          <Box
            component="img"
            src={LIVESTREAM_EYE_SRC}
            alt=""
            aria-hidden
            sx={{
              width: 16,
              height: 16,
              display: "block",
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            component="span"
            sx={{
              color: "rgba(255,255,255,0.95)",
              fontWeight: 600,
              fontSize: "0.8rem",
            }}
          >
            {liveCount}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/** Viewer-only stage: subscribed remote camera + screen, no local capture UI. */
function LiveStreamViewerStage({ stream }) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: true }
  );

  const bgPath = lobbyBackgroundPath(stream);
  const bgUrl = buildMediaUrl(bgPath);
  const avatarUrl = buildMediaUrl(stream?.user?.avatar);
  const streamTitle = displayNameFromStream(stream);
  const reactionCount = stream?.reactionCount ?? 0;
  const commentCount = stream?.commentCount ?? 0;

  if (!tracks.length) {
    return (
      <Box
        sx={{
          position: "relative",
          height: "100%",
          minHeight: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: "#0b0f19",
        }}
      >
        {bgUrl ? (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${bgUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transform: "scale(1.08)",
              filter: "blur(28px)",
            }}
          />
        ) : null}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(11,15,25,0.55) 0%, rgba(11,15,25,0.85) 100%)",
          }}
        />
        <HostPresencePill stream={stream} top={16} />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Avatar
            src={avatarUrl || undefined}
            alt={streamTitle || "Live stream"}
            sx={{
              width: 112,
              height: 112,
              border: "4px solid rgba(255,255,255,0.95)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
              bgcolor: "rgba(255,255,255,0.12)",
              fontSize: "2.25rem",
              fontWeight: 600,
            }}
          >
            {streamTitle?.charAt(0)}
          </Avatar>
        </Box>
        <Box
          component="aside"
          aria-label={`Reactions ${reactionCount}, comments ${commentCount}, send`}
          sx={{
            position: "absolute",
            right: 20,
            bottom: 28,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            color: "#fff",
          }}
        >
          <Box
            component="img"
            src={LIVESTREAM_REACTION_SRC}
            alt=""
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              display: "block",
              objectFit: "contain",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              opacity: 0.95,
              lineHeight: 1.2,
              mb: 1.25,
              mt: 1,
            }}
          >
            {reactionCount}
          </Typography>
          <Box
            component="img"
            src={LIVESTREAM_COMMENT_SRC}
            alt=""
            aria-hidden
            sx={{
              width: 32,
              height: 32,
              display: "block",
              objectFit: "contain",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              opacity: 0.95,
              lineHeight: 1.2,
              mb: 1.25,
              mt: 1,
            }}
          >
            {commentCount}
          </Typography>
          <Box
            component="img"
            src={LIVESTREAM_SEND_SRC}
            alt=""
            aria-hidden
            sx={{
              width: 32,
              height: 32,
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <HostPresencePill stream={stream} top={16} />
      <GridLayout tracks={tracks} style={{ height: "100%" }}>
        <ParticipantTile />
      </GridLayout>
    </Box>
  );
}

const LiveStream = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const contentIdParam = urlParams.get("id");

  const originalText = useMemo(() => {
    if (!contentIdParam || !process.env.REACT_APP_SECRET_KEY) return null;
    try {
      const fixedId = contentIdParam.replace(/ /g, "+");
      const bytes = CryptoJS.AES.decrypt(
        fixedId,
        process.env.REACT_APP_SECRET_KEY
      );
      const text = bytes.toString(CryptoJS.enc.Utf8);
      return text || null;
    } catch {
      return null;
    }
  }, [contentIdParam]);

  const [stream, setStream] = useState(undefined);
  const [showAppBanner, setShowAppBanner] = useState(true);
  const [device, setDevice] = useState(null);
  const [liveKitUrl, setLiveKitUrl] = useState(null);
  const [liveKitToken, setLiveKitToken] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [joinPending, setJoinPending] = useState(false);
  const [joinError, setJoinError] = useState(null);

  useEffect(() => {
    const detectedDevice = detectDevice("NextFriend");
    setDevice(detectedDevice);
  }, []);

  useEffect(() => {
    if (!originalText) return;
    setLoadError(null);
    fetchLiveStream(originalText)
      .then((result) => {
        setStream(result);
      })
      .catch((e) => {
        const msg =
          e?.response?.data?.message || e?.message || "Failed to load";
        setLoadError(msg);
        if (typeof e?.response?.data?.message === "string") {
          alert(e.response.data.message);
        }
      });
  }, [originalText]);

  useEffect(() => {
    if (!stream?.id) return;

    let cancelled = false;
    setJoinPending(true);
    setJoinError(null);

    joinLivestream(stream.id)
      .then((data) => {
        if (cancelled) return;
        const { url, token } = pickJoinCredentials(data);
        setLiveKitUrl(url || null);
        setLiveKitToken(token || null);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Could not join live stream";
        setJoinError(msg);
        setLiveKitUrl(null);
        setLiveKitToken(null);
      })
      .finally(() => {
        if (!cancelled) setJoinPending(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stream?.id]);

  const handleDownloadApp = () => {
    if (device?.storeUrl) {
      try {
        window.location.href = device.storeUrl;
      } catch {
        window.open(device.storeUrl, "_blank");
      }
    }
  };

  const closeBanner = () => setShowAppBanner(false);

  const bgPath = lobbyBackgroundPath(stream);
  const bgUrl = buildMediaUrl(bgPath);
  const avatarUrl = buildMediaUrl(stream?.user?.avatar);
  const streamTitle = displayNameFromStream(stream);

  if (!contentIdParam || !originalText) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#0b0f19",
          px: 2,
        }}
      >
        <Typography sx={{ color: "#fff", textAlign: "center" }} variant="h6">
          Invalid or missing live stream link.
        </Typography>
      </Box>
    );
  }

  if (loadError && !stream) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#0b0f19",
          px: 2,
        }}
      >
        <Typography sx={{ color: "#fff", textAlign: "center" }} variant="h6">
          {loadError}
        </Typography>
      </Box>
    );
  }

  if (stream === undefined) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#0b0f19",
        }}
      >
        <CircularProgress sx={{ color: "#6E41BF" }} />
      </Box>
    );
  }

  const lobby = (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        bgcolor: "#0b0f19",
      }}
    >
      {showAppBanner && device && device.type !== "other" && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            backdropFilter: "blur(10px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Box
              sx={{
                marginRight: "12px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src="/nextfriend_logo.svg"
                alt="NextFriend Logo"
                width="32"
                height="32"
              />
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "white", fontWeight: 600, marginBottom: "2px" }}
              >
                Get the NextFriend App
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                Better experience on mobile
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={handleDownloadApp}
              sx={{
                backgroundColor: "#6E41BF",
                color: "white",
                textTransform: "none",
                borderRadius: "20px",
                fontSize: "12px",
                padding: "6px 16px",
                "&:hover": { backgroundColor: "#6E41BF" },
              }}
            >
              Download
            </Button>
            <IconButton
              size="small"
              onClick={closeBanner}
              sx={{ color: "rgba(255, 255, 255, 0.7)" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {bgUrl ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bgUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: "scale(1.08)",
            filter: "blur(28px)",
          }}
        />
      ) : null}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(11,15,25,0.55) 0%, rgba(11,15,25,0.85) 100%)",
        }}
      />

      <HostPresencePill
        stream={stream}
        top={showAppBanner && device && device.type !== "other" ? 72 : 16}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          textAlign: "center",
        }}
      >
        {stream?.status === "live" && (
          <Typography
            variant="overline"
            sx={{
              color: "#E82222",
              letterSpacing: 3,
              fontWeight: 700,
              mb: 2,
            }}
          >
            LIVE
          </Typography>
        )}
        <Avatar
          src={avatarUrl || undefined}
          alt={streamTitle}
          sx={{
            width: 112,
            height: 112,
            border: "4px solid rgba(255,255,255,0.95)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
            bgcolor: "rgba(255,255,255,0.12)",
            fontSize: "2.25rem",
            fontWeight: 600,
          }}
        >
          {streamTitle?.charAt(0)}
        </Avatar>
        <Typography
          variant="h5"
          sx={{
            mt: 2.5,
            color: "#fff",
            fontWeight: 600,
            maxWidth: 320,
            lineHeight: 1.3,
          }}
        >
          {streamTitle || "Live stream"}
        </Typography>
        {joinPending ? (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: "#6E41BF" }} />
          </Box>
        ) : null}
        {!joinPending && joinError ? (
          <Typography
            variant="body2"
            sx={{ mt: 2, color: "#ffb4b4", maxWidth: 320 }}
          >
            {joinError}
          </Typography>
        ) : null}
        {!joinPending && !joinError && (!liveKitUrl || !liveKitToken) ? (
          <Typography
            variant="body2"
            sx={{ mt: 2, color: "rgba(255,255,255,0.65)", maxWidth: 280 }}
          >
            Waiting for playback… If this persists, open this stream in the app.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );

  if (liveKitUrl && liveKitToken) {
    return (
      <div style={containerStyle}>
        <LiveKitRoom
          serverUrl={liveKitUrl}
          token={liveKitToken}
          connect={true}
          video={false}
          audio={false}
          screen={false}
          style={{ height: "100%", width: "100%" }}
        >
          <LiveStreamViewerStage stream={stream} />
          <RoomAudioRenderer />
        </LiveKitRoom>
      </div>
    );
  }

  return lobby;
};

export default LiveStream;
